import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar, Shield, AlertTriangle, Database, Clock } from 'lucide-react'
import { Graph } from '@/lib/api'

interface BreachTimelineProps {
  graph: Graph
  query: string
  profileData?: any
}

interface BreachEvent {
  id: string
  date: string
  breachName: string
  description: string
  recordsAffected: number
  dataTypes: string[]
  severity: 'low' | 'medium' | 'high' | 'critical'
  source: string
}

export function BreachTimeline({ graph, query, profileData }: BreachTimelineProps): JSX.Element {
  // Extract breach events from the graph or profileData
  const extractBreachEvents = (): BreachEvent[] => {
    const events: BreachEvent[] = []
    
    // First try to use profileData.breachSources if available (more reliable)
    if (profileData && profileData.breachSources && profileData.breachSources.length > 0) {
      console.log(`Extracting breach events from ${profileData.breachSources.length} breach sources`)
      
      profileData.breachSources.forEach((breach: any, index: number) => {
        const breachName = breach.source || 'Unknown Breach'
        
        // If this breach has multiple records (allRecords), create separate events for each unique record
        // Otherwise, create one event per breach
        if (breach.allRecords && Array.isArray(breach.allRecords) && breach.allRecords.length > 1) {
          // Create events for each record, but group similar ones
          const uniqueRecords = new Map<string, any>()
          
          breach.allRecords.forEach((record: any, recordIndex: number) => {
            // Create a unique key based on key fields to avoid duplicates
            const recordKey = `${record.Email || ''}_${record.Phone || ''}_${record.FullName || ''}_${record.IP || ''}`
            
            if (!uniqueRecords.has(recordKey) || uniqueRecords.size === 0) {
              uniqueRecords.set(recordKey || `record_${recordIndex}`, record)
            }
          })
          
          // If we have many unique records, show them as separate timeline entries
          // Show all records - no limit
          const recordsToShow = Array.from(uniqueRecords.values())
          
          recordsToShow.forEach((record: any, recordIndex: number) => {
            const recordsAffected = parseInt(breach.records) || breach.allRecords.length
            const dataTypes = Object.keys(record).filter(k => record[k] && k !== 'Date' && k !== 'RegDate')
            
            let severity: 'low' | 'medium' | 'high' | 'critical' = 'low'
            if (recordsAffected > 10000000) severity = 'critical'
            else if (recordsAffected > 1000000) severity = 'high'
            else if (recordsAffected > 100000) severity = 'medium'
            else if (recordsAffected > 1000) severity = 'medium'
            
            // Parse and validate date
            let recordDate = record.Date || record.RegDate || breach.date || breach.breachDescription || 'Unknown Date'
            // If date is not a valid format, use breach date or default
            if (recordDate && recordDate !== 'Unknown Date') {
              const testDate = new Date(recordDate)
              if (isNaN(testDate.getTime())) {
                recordDate = breach.date || breach.breachDescription || 'Unknown Date'
              }
            }
            
            events.push({
              id: `breach-${breachName}-record-${recordIndex}`,
              date: recordDate,
              breachName: breachName,
              description: `Record ${recordIndex + 1} from ${breachName}: ${dataTypes.slice(0, 3).join(', ')} exposed`,
              recordsAffected: 1,
              dataTypes: dataTypes.length > 0 ? dataTypes : ['Email, Password, Personal Data'],
              severity: severity,
              source: breachName
            })
          })
          
          // No summary entry needed - all records are shown
          events.push({
            id: `breach-${breachName}-summary`,
            date: breach.date || 'Unknown Date',
            breachName: `${breachName} (${breach.allRecords.length} total records)`,
            description: `Total of ${breach.allRecords.length} records exposed in this breach`,
            recordsAffected: breach.allRecords.length,
            dataTypes: breach.dataTypes ? breach.dataTypes.split(', ') : ['Email, Password, Personal Data'],
            severity: 'high',
            source: breachName
          })
        } else {
          // Single breach entry
          const recordsAffected = parseInt(breach.records) || breach.numOfResults || 1
          const dataTypes = breach.dataTypes ? breach.dataTypes.split(', ') : ['Email, Password, Personal Data']
          
          let severity: 'low' | 'medium' | 'high' | 'critical' = 'low'
          if (recordsAffected > 10000000) {
            severity = 'critical'
          } else if (recordsAffected > 1000000) {
            severity = 'high'
          } else if (recordsAffected > 100000) {
            severity = 'medium'
          } else if (recordsAffected > 1000) {
            severity = 'medium'
          }
          
          events.push({
            id: `breach-${breachName}-${index}`,
            date: breach.date || 'Unknown Date',
            breachName: breachName,
            description: breach.breachDescription || `Data breach affecting ${recordsAffected} records`,
            recordsAffected: recordsAffected,
            dataTypes: dataTypes,
            severity: severity,
            source: breachName
          })
        }
      })
    } else if (graph && graph.nodes) {
      // Fallback to graph nodes
      const breachNodes = graph.nodes.filter((node: any) => {
        if (node.props?.node_type === 'breach' || node.props?.breach_name) {
          return true
        }
        if (node.label && (node.label.includes('Breach:') || node.label.startsWith('Breach:'))) {
          return true
        }
        if (node.type === 'source' && node.props?.breach_name) {
          return true
        }
        return false
      })

      breachNodes.forEach((node: any) => {
        const breachName = node.props.breach_name || 'Unknown Breach'
        const breachDate = node.props.breach_date || 'Unknown Date'
        const recordsAffected = node.props.records_affected || 0
        const dataTypes = (node.props.data_types || 'Email, Password, Personal Data').split(', ')
        
        let severity: 'low' | 'medium' | 'high' | 'critical' = 'low'
        if (recordsAffected > 10000000) severity = 'critical'
        else if (recordsAffected > 1000000) severity = 'high'
        else if (recordsAffected > 100000) severity = 'medium'

        events.push({
          id: node.id,
          date: breachDate,
          breachName: breachName,
          description: node.props.breach_description || `Data breach affecting ${recordsAffected.toLocaleString()} records`,
          recordsAffected: recordsAffected,
          dataTypes: dataTypes,
          severity: severity,
          source: node.source || 'Unknown'
        })
      })
    }

    // Sort by date (newest first), handling invalid dates
    return events.sort((a, b) => {
      try {
        const dateA = a.date === 'Unknown Date' ? 0 : new Date(a.date).getTime()
        const dateB = b.date === 'Unknown Date' ? 0 : new Date(b.date).getTime()
        // If either date is invalid, put it at the end
        if (isNaN(dateA) && isNaN(dateB)) return 0
        if (isNaN(dateA)) return 1
        if (isNaN(dateB)) return -1
        return dateB - dateA
      } catch {
        return 0
      }
    })
  }

  const breachEvents = extractBreachEvents()

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white'
      case 'high': return 'bg-orange-500 text-white'
      case 'medium': return 'bg-yellow-500 text-black'
      case 'low': return 'bg-green-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />
      case 'high': return <Shield className="w-4 h-4" />
      case 'medium': return <Database className="w-4 h-4" />
      case 'low': return <Clock className="w-4 h-4" />
      default: return <Database className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    if (dateString === 'Unknown Date' || !dateString || dateString.trim() === '') return 'Unknown Date'
    try {
      const date = new Date(dateString)
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown Date'
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return 'Unknown Date'
    }
  }

  if (breachEvents.length === 0) {
    return (
      <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-0 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
              <Calendar className="w-4 h-4 text-white" />
            </div>
            Breach Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">No Breach Timeline Available</h3>
            <p className="text-gray-500 dark:text-gray-400">
              No breach data found for <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{query}</span>
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-0 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
        <CardTitle className="flex items-center gap-3 text-xl">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
            <Calendar className="w-4 h-4 text-white" />
          </div>
          Breach Timeline
        </CardTitle>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Chronological view of data breaches affecting <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{query}</span>
        </p>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {breachEvents.map((event, index) => (
            <div key={event.id} className="relative">
              {/* Timeline line */}
              {index < breachEvents.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-full bg-gradient-to-b from-blue-200 to-transparent dark:from-blue-800"></div>
              )}
              
              {/* Timeline item */}
              <div className="flex items-start gap-4">
                {/* Timeline dot */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getSeverityColor(event.severity)} shadow-lg`}>
                  {getSeverityIcon(event.severity)}
                </div>
                
                {/* Event content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {event.breachName}
                    </h3>
                    <Badge variant="outline" className={getSeverityColor(event.severity)}>
                      {event.severity.toUpperCase()}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(event.date)}</span>
                    <span>•</span>
                    <span>{event.recordsAffected.toLocaleString()} records affected</span>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    {event.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {event.dataTypes.map((dataType, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {dataType}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Source: {event.source}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Summary */}
        <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Timeline Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-300">Total Breaches:</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">{breachEvents.length}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-300">Total Records:</span>
              <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                {breachEvents.reduce((sum, event) => sum + event.recordsAffected, 0).toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-300">Critical:</span>
              <span className="ml-2 font-semibold text-red-600">
                {breachEvents.filter(e => e.severity === 'critical').length}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-300">High:</span>
              <span className="ml-2 font-semibold text-orange-600">
                {breachEvents.filter(e => e.severity === 'high').length}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
